export const ACCOUNTING_ACCOUNT_TYPES = ['TITLE', 'SUBTITLE', 'MAJOR'] as const;

export type AccountingAccountType = (typeof ACCOUNTING_ACCOUNT_TYPES)[number];

export type AccountingAccountNature =
  | 'ASSET_DEBIT'
  | 'ASSET_CREDIT'
  | 'LIABILITY_DEBIT'
  | 'LIABILITY_CREDIT'
  | 'CAPITAL_DEBIT'
  | 'CAPITAL_CREDIT'
  | 'RESULTS_DEBIT'
  | 'RESULTS_CREDIT'
  | 'STATISTICAL_DEBIT'
  | 'STATISTICAL_CREDIT'
  | 'MEMORANDUM_DEBIT'
  | 'MEMORANDUM_CREDIT';

export type AccountingAccountParentDto = {
  uuid: string;
  accountNumber: string;
  formattedAccountNumber: string;
  name: string;
  type: AccountingAccountType;
};

export type AccountingAccountDto = {
  uuid: string;
  accountNumber: string;
  formattedAccountNumber: string;
  name: string;
  type: AccountingAccountType;
  typeUuid: string | null;
  typeName: string | null;
  nature: AccountingAccountNature | string | null;
  natureUuid: string | null;
  natureName: string | null;
  parent: AccountingAccountParentDto | null;
  createdAt?: string;
  updatedAt?: string;
};

export type AccountingAccountTreeNodeDto = {
  uuid: string;
  accountNumber: string;
  formattedAccountNumber: string;
  name: string;
  type: AccountingAccountType;
  nature: AccountingAccountNature | string | null;
  children: AccountingAccountTreeNodeDto[];
};

export type AccountingAccountCreateRequest = {
  accountNumber: string;
  name: string;
  typeUuid: string;
  parentUuid: string | null;
  natureUuid: string | null;
};

export type AccountingAccountUpdateRequest = {
  accountNumber: string;
  name: string;
  parentUuid: string | null;
  natureUuid: string | null;
};

export type AccountingAccountSelectOption = {
  name: string;
  value: string;
  code: string;
};

export type AccountingAccountFormSelects = {
  types: AccountingAccountSelectOption[];
  natures: AccountingAccountSelectOption[];
  titleParents: AccountingAccountParentDto[];
  subtitleParents: AccountingAccountParentDto[];
};

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

export function isAccountingAccountType(
  value: unknown,
): value is AccountingAccountType {
  return (
    typeof value === 'string' &&
    (ACCOUNTING_ACCOUNT_TYPES as readonly string[]).includes(value)
  );
}

const TYPE_BY_LABEL: Record<string, AccountingAccountType> = {
  titulo: 'TITLE',
  título: 'TITLE',
  subtitulo: 'SUBTITLE',
  subtítulo: 'SUBTITLE',
  mayor: 'MAJOR',
};

const NATURE_BY_LABEL: Record<string, string> = {
  'activo deudora': 'ASSET_DEBIT',
  'activo acreedora': 'ASSET_CREDIT',
  'pasivo deudora': 'LIABILITY_DEBIT',
  'pasivo acreedora': 'LIABILITY_CREDIT',
  'capital deudora': 'CAPITAL_DEBIT',
  'capital acreedora': 'CAPITAL_CREDIT',
  'resultados deudora': 'RESULTS_DEBIT',
  'resultados acreedora': 'RESULTS_CREDIT',
  'estadisticas deudora': 'STATISTICAL_DEBIT',
  'estadísticas deudora': 'STATISTICAL_DEBIT',
  'estadisticas acreedora': 'STATISTICAL_CREDIT',
  'estadísticas acreedora': 'STATISTICAL_CREDIT',
  'orden deudora': 'MEMORANDUM_DEBIT',
  'orden acreedora': 'MEMORANDUM_CREDIT',
};

function asNamedRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function normalizeLabel(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Acepta el enum documentado (`TITLE`) o el objeto vivo `{ uuid, name: "Título" }`.
 */
export function resolveAccountingAccountType(
  value: unknown,
): AccountingAccountType | null {
  if (isAccountingAccountType(value)) return value;

  const record = asNamedRecord(value);
  if (!record) return null;

  if (isAccountingAccountType(record['value'])) return record['value'];
  if (isAccountingAccountType(record['code'])) return record['code'];

  const name = typeof record['name'] === 'string' ? normalizeLabel(record['name']) : '';
  return TYPE_BY_LABEL[name] ?? null;
}

/**
 * Acepta el enum documentado (`ASSET_DEBIT`) o el objeto vivo `{ uuid, name }`.
 */
export function resolveAccountingAccountNature(value: unknown): string | null {
  if (isAccountingAccountType(value)) return null;
  if (typeof value === 'string' && value) {
    return NATURE_BY_LABEL[normalizeLabel(value)] ?? value;
  }

  const record = asNamedRecord(value);
  if (!record) return null;

  if (typeof record['value'] === 'string' && record['value']) {
    return NATURE_BY_LABEL[normalizeLabel(record['value'])] ?? record['value'];
  }
  if (typeof record['code'] === 'string' && record['code']) return record['code'];

  const name = typeof record['name'] === 'string' ? normalizeLabel(record['name']) : '';
  return NATURE_BY_LABEL[name] ?? null;
}

export function readCatalogUuid(value: unknown): string | null {
  const record = asNamedRecord(value);
  return typeof record?.['uuid'] === 'string' ? record['uuid'] : null;
}

export function readCatalogName(value: unknown): string | null {
  const record = asNamedRecord(value);
  return typeof record?.['name'] === 'string' && record['name']
    ? record['name']
    : null;
}

/** UUID del catálogo vivo, o el enum documentado si no hay uuid. */
export function catalogWriteValue(
  raw: unknown,
  resolvedCode: string | null,
): string | null {
  const uuid = readCatalogUuid(raw);
  if (uuid) return uuid;

  const record = asNamedRecord(raw);
  if (typeof record?.['value'] === 'string' && record['value']) return record['value'];
  return resolvedCode;
}
